import axios from 'axios'
import * as React from 'react'
import { useEffect, useState } from 'react'
import ReactToPrint, { useReactToPrint } from 'react-to-print'
import RemarkPasswordModal from './RemarkPasswordModal'

// Unique key for an attendee row (main user vs. an additional order product).
const attKey = (a) => `${a.type}:${a.id}`

const AccessRight = ({ user, printContentRef }) => {
  const [status, setStatus] = useState(user.collectionStatus || {})
  // Gala attendees (main user first, then additional ticket holders). Kept in
  // state so keyed-in ticket numbers reflect immediately after collection.
  const [attendees, setAttendees] = useState(user.galaAttendees || [])
  // key of the action currently in-flight ('' when idle), used to disable buttons
  const [loading, setLoading] = useState('')

  // Password gate shown before a name badge reprint.
  const [showReprintGate, setShowReprintGate] = useState(false)

  // Dinner ticket modal state.
  const [showDinnerModal, setShowDinnerModal] = useState(false)
  const [ticketInputs, setTicketInputs] = useState({})
  const [savingDinner, setSavingDinner] = useState(false)

  const ownedAccess = (user.eventAccessList || []).filter((a) => a.hasAccess)
  // Only attendees with in-person access may print a name badge / collect a
  // lanyard bag; without it those cards read "Not Purchased".
  const canPrint = !!user.canPrintNameBadge
  // The dinner ticket can be collected unless the delegate explicitly RSVP'd
  // "not attending". Attending — or no RSVP at all — both allow collection.
  const galaDinnerDeclined = !!user.galaDinnerNotAttending
  const mainAttendee = attendees.find((a) => a.isMainUser)
  // Every gala attendee must have a ticket number before the counter can save
  // and mark the dinner ticket collected.
  const allTicketsFilled = attendees.every(
    (a) => (ticketInputs[attKey(a)] ?? '').trim() !== ''
  )

  // Re-sync when a different delegate is scanned (component is reused, not remounted).
  useEffect(() => {
    setStatus(user.collectionStatus || {})
    setAttendees(user.galaAttendees || [])
    setShowReprintGate(false)
  }, [user.id])

  const renderFlag = (bool) =>
    bool ? (
      <i className="fas fa-check" style={{ color: 'green' }}></i>
    ) : (
      <i className="fas fa-times" style={{ color: 'red' }}></i>
    )

  // Light-red card shown when a collection card doesn't apply to this delegate
  // (e.g. no physical ticket, or not attending the gala dinner).
  const notApplicableCard = (label) => (
    <div className="p-4 mt-2 card w-100" style={{ backgroundColor: '#f8d7da' }}>
      <span style={{ color: '#842029' }}>
        <i className="fas fa-times me-2"></i>
        {label}
      </span>
    </div>
  )

  // Distinct badge colour per ticket category (escalating tier).
  const categoryBadgeClass = (cat) =>
    `badge ${
      { Regular: 'bg-secondary', VIP: 'bg-warning text-dark', VVIP: 'bg-danger' }[
        cat
      ] || 'bg-info'
    }`

  const categoryBadge = (cat) =>
    cat ? (
      <span className={categoryBadgeClass(cat)}>{cat}</span>
    ) : (
      <span className="text-muted">-</span>
    )

  // After a physical print: the first print marks the badge collected, any
  // subsequent print marks it reprinted (mirrors Api::UsersController#print_badge).
  const handlePrinted = () => {
    axios
      .get(`/api/users/print_badge?id=${user.id}`)
      .then(() => {
        setStatus((prev) =>
          prev.badgeCollected
            ? { ...prev, badgeReprinted: true }
            : { ...prev, badgeCollected: true }
        )
      })
      .catch((err) => alert(err))
  }

  // Print trigger — only rendered for delegates who can print a badge (the card
  // is replaced with a "Not Purchased" notice otherwise).
  const printButton = (label) => (
    <ReactToPrint
      onAfterPrint={handlePrinted}
      trigger={() => <button className="btn btn-sm btn-danger">{label}</button>}
      content={() => printContentRef?.current}
    />
  )

  // Reprints are password-protected for every delegate, so the reprint button
  // opens the onsite-remark password modal and fires this once it's unlocked.
  const triggerReprint = useReactToPrint({
    onAfterPrint: handlePrinted,
    content: () => printContentRef?.current,
  })

  // Mark the merged Lanyard + Goodie Bag as collected on the user.
  const collectLanyardBag = () => {
    setLoading('lanyardBag')
    axios
      .post('/api/users/collect_lanyard_bag', { id: user.id })
      .then(({ data }) => {
        if (!data?.success) {
          alert(data?.message)
          return
        }
        setStatus((prev) => ({
          ...prev,
          lanyardBagCollected: true,
        }))
      })
      .catch((err) => alert(err))
      .finally(() => setLoading(''))
  }

  // Seed the modal inputs from the current ticket numbers, then open it.
  const openDinnerModal = () => {
    const seeded = {}
    attendees.forEach((a) => {
      seeded[attKey(a)] = a.ticketNumber || ''
    })
    setTicketInputs(seeded)
    setShowDinnerModal(true)
  }

  // Save the keyed-in ticket numbers for every gala attendee and mark collected.
  const saveDinnerTickets = () => {
    setSavingDinner(true)
    const tickets = attendees.map((a) => ({
      type: a.type,
      id: a.id,
      number: ticketInputs[attKey(a)] ?? '',
    }))
    axios
      .post('/api/users/collect_dinner_ticket', { id: user.id, tickets })
      .then(({ data }) => {
        if (!data?.success) {
          alert(data?.message)
          return
        }
        setAttendees((prev) =>
          prev.map((a) => ({
            ...a,
            ticketNumber: ticketInputs[attKey(a)] ?? a.ticketNumber,
          }))
        )
        setStatus((prev) => ({ ...prev, dinnerCollected: true }))
        setShowDinnerModal(false)
      })
      .catch((err) => alert(err))
      .finally(() => setSavingDinner(false))
  }

  return (
    <>
      {/* 1. Name badge collected — print button lives here until collected */}
      <div className="mt-3">Name Badge Collected:</div>
      {!canPrint ? (
        notApplicableCard('Not Purchased')
      ) : (
        <div className="p-4 mt-2 card w-100">
          {status.badgeCollected ? (
            <span>
              {renderFlag(true)}
              <small className="ms-2">Collected</small>
            </span>
          ) : (
            printButton('Click Here to Print')
          )}
        </div>
      )}

      {/* 2. Name badge reprinted — reprint button appears once collected */}
      <div className="mt-3">Name Badge Reprinted:</div>
      {!canPrint ? (
        notApplicableCard('Not Purchased')
      ) : (
        <div className="p-4 mt-2 card w-100">
          {status.badgeReprinted ? (
            <span>
              {renderFlag(true)}
              <small className="ms-2">Reprinted</small>
            </span>
          ) : status.badgeCollected ? (
            <button
              className="btn btn-sm btn-danger"
              onClick={() => setShowReprintGate(true)}
            >
              <i className="fas fa-lock me-2"></i>
              Click Here to Reprint
            </button>
          ) : (
            <span>
              {renderFlag(false)}
              <small className="ms-2 text-muted">No reprints</small>
            </span>
          )}
        </div>
      )}

      {/* 3. Lanyard and congress bag collected */}
      <div className="mt-3">Lanyard &amp; Congress Bag Collected:</div>
      {!canPrint ? (
        notApplicableCard('Not Purchased')
      ) : (
        <div className="p-4 mt-2 card w-100">
          {status.lanyardBagCollected ? (
            <span>
              {renderFlag(true)}
              <small className="ms-2">Collected</small>
            </span>
          ) : (
            <button
              className="btn btn-sm btn-outline-green"
              disabled={!!loading}
              onClick={collectLanyardBag}
            >
              {loading === 'lanyardBag' ? 'Marking...' : 'Collect'}
            </button>
          )}
        </div>
      )}

      {/* 4. Dinner ticket — merged number + category, with the collect modal */}
      <div className="mt-3">Dinner Ticket:</div>
      {galaDinnerDeclined ? (
        notApplicableCard('Not Attending')
      ) : (
        <div className="p-4 mt-2 card w-100">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div>
                Number:{' '}
                <span className="fw-bold">
                  {mainAttendee?.ticketNumber || '-'}
                </span>
              </div>
              <div>Category: {categoryBadge(mainAttendee?.ticketCategory)}</div>
            </div>
            {status.dinnerCollected ? (
              <span>
                {renderFlag(true)}
                <small className="ms-2">Collected</small>
              </span>
            ) : (
              <button
                className="btn btn-sm btn-outline-green"
                onClick={openDinnerModal}
              >
                Collect
              </button>
            )}
          </div>
        </div>
      )}

      {/* 5. Gala dinner attendees — main user + additional ticket holders */}
      <div className="mt-3">Gala Dinner Attendees ({attendees.length}):</div>
      <div className="p-4 mt-2 card w-100">
        {attendees.map((a, i) => (
          <div
            key={attKey(a)}
            className={`pb-2 mb-2 ${
              i < attendees.length - 1 ? 'border-bottom' : ''
            }`}
          >
            <div className="fw-bold">
              {a.isMainUser && (
                <span className="badge bg-success me-2">Main</span>
              )}
              {a.isMainUser
                ? `${a.nameForBadge || '(no name)'} (${a.fullName})`
                : a.fullName || '(no name)'}
            </div>
            <small className="text-muted d-block">
              Ticket #: {a.ticketNumber || '-'}
            </small>
            <small className="d-block">
              Category: {categoryBadge(a.ticketCategory)}
            </small>
            {a.email && <small className="text-muted d-block">{a.email}</small>}
            {a.mealPreference && (
              <small className="text-muted d-block">
                Meal: {a.mealPreference}
              </small>
            )}
          </div>
        ))}
      </div>

      {/* 6. Event access — owned access flags, labelled per Product::PRODUCT_NAMES */}
      <div className="mt-3">Event Access:</div>
      <div className="p-4 mt-2 card w-100">
        {ownedAccess.length > 0 ? (
          ownedAccess.map((access, i) => (
            <div key={access.name || i} className="mb-1">
              {renderFlag(true)}
              <small className="ms-2">{access.label}</small>
            </div>
          ))
        ) : (
          <small className="text-muted">No access</small>
        )}
      </div>

      {/* Password gate in front of the name badge reprint */}
      {showReprintGate && (
        <RemarkPasswordModal
          title="Restricted — Name Badge Reprint"
          onCancel={() => setShowReprintGate(false)}
          onSuccess={() => {
            setShowReprintGate(false)
            triggerReprint()
          }}
        />
      )}

      {/* Dinner ticket collection modal */}
      {showDinnerModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050,
          }}
          onClick={() => !savingDinner && setShowDinnerModal(false)}
        >
          <div
            className="p-4 card"
            style={{
              background: '#fff',
              width: '90%',
              maxWidth: 500,
              maxHeight: '85vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h5 className="mb-1">Enter Dinner Ticket Numbers</h5>
            <small className="mb-3 text-muted d-block">
              Categories are managed in the admin user form.
            </small>
            {attendees.map((a) => (
              <div key={attKey(a)} className="mb-3">
                <label className="mb-1 d-block fw-bold">
                  {a.fullName || '(no name)'}
                  {a.isMainUser && (
                    <span className="badge bg-success ms-2">Main</span>
                  )}
                  {a.ticketCategory && (
                    <span className={`${categoryBadgeClass(a.ticketCategory)} ms-2`}>
                      {a.ticketCategory}
                    </span>
                  )}
                </label>
                {a.mealPreference && (
                  <div className="mb-1 fs-5 text-muted">
                    Meal: {a.mealPreference}
                  </div>
                )}
                <input
                  className="form-control"
                  placeholder="Ticket number"
                  value={ticketInputs[attKey(a)] ?? ''}
                  onChange={(e) =>
                    setTicketInputs((prev) => ({
                      ...prev,
                      [attKey(a)]: e.target.value,
                    }))
                  }
                />
              </div>
            ))}
            <div className="mt-3 text-end">
              <button
                className="btn btn-sm btn-secondary me-2"
                onClick={() => setShowDinnerModal(false)}
                disabled={savingDinner}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm btn-success"
                onClick={saveDinnerTickets}
                disabled={savingDinner || !allTicketsFilled}
              >
                {savingDinner ? 'Saving...' : 'Save & Mark Collected'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AccessRight
